/**
 * Holding Drill-Down Modal Component
 * Shows which clients/portfolios hold a specific security
 * Click client → navigates to Client Level View
 */

import React from 'react';
import { X, Eye, TrendingUp, Info } from 'lucide-react';
import { TopHolding } from '../../hooks/useMyBookData';
import Badge from './atoms/Badge';

// Extended TopHolding with optional breakdown (from backend when available)
interface HoldingBreakdown {
  customerId: string;
  customerName: string;
  portfolioId: string;
  portfolioName: string;
  quantity: number;
  value: number;
  percentOfPortfolio: number;
}

interface TopHoldingWithBreakdown extends TopHolding {
  breakdown?: HoldingBreakdown[];
}

interface HoldingDrillDownProps {
  holding: TopHoldingWithBreakdown | null;
  isOpen: boolean;
  onClose: () => void;
  onClientClick?: (customerId: string) => void;
}

const HoldingDrillDown: React.FC<HoldingDrillDownProps> = ({
  holding,
  isOpen,
  onClose,
  onClientClick
}) => {
  if (!isOpen || !holding) return null;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // Format quantity with decimals
  const formatQuantity = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-gray-900">
                    {holding.securityName}
                  </h2>
                  <Badge label={holding.securityType} variant="info" size="sm" />
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="font-mono">{holding.isin}</span>
                  <span className="text-gray-400">|</span>
                  <span>{holding.sector}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(holding.totalValue)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Total Value</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {formatPercent(holding.percentOfBook)}
                </div>
                <div className="text-xs text-gray-500 mt-1">% of Book</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {holding.clientCount}
                </div>
                <div className="text-xs text-gray-500 mt-1">Clients</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {holding.portfolioCount}
                </div>
                <div className="text-xs text-gray-500 mt-1">Portfolios</div>
              </div>
            </div>
          </div>

          {/* Client Breakdown Table */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
            {holding.breakdown && holding.breakdown.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Portfolio
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % of Portfolio
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {holding.breakdown.map((breakdown, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {breakdown.customerName}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {breakdown.customerId.slice(0, 12)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {breakdown.portfolioName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {breakdown.portfolioId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatQuantity(breakdown.quantity)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(breakdown.value)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min(breakdown.percentOfPortfolio, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-14 text-right">
                            {formatPercent(breakdown.percentOfPortfolio)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            onClientClick?.(breakdown.customerId);
                            onClose();
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="View Client Portfolio"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center">
                <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Breakdown Not Available
                </h4>
                <p className="text-gray-600 max-w-md mx-auto">
                  This holding is spread across {holding.clientCount} client(s) and {holding.portfolioCount} portfolio(s).
                  Detailed breakdown will be available in a future update.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <TrendingUp className="w-4 h-4" />
                <span>{holding.portfolioCount} portfolio(s) across {holding.clientCount} client(s)</span>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HoldingDrillDown;
